import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  api,
  type AgentCardItem,
  type AgentCardsState,
  type PullGachaResult,
} from "@/lib/api";
import { authClient } from "@/lib/auth";
import { resolveAuthenticated } from "@/lib/devConfig";
import { patchProfileSummary, seedProfileSummary } from "@/components/layout/useProfile";
import { seedAgentCardsCache } from "@/components/collection/useCollectionData";
import type { RevealCard } from "@/components/draw/RevealOverlay";

export const EMPTY_CARDS_STATE: AgentCardsState = {
  items: [],
  ownedCount: 0,
  totalCount: 0,
  dailyFreeAvailable: false,
  nextDailyFreeAt: null,
};

type DrawSnapshot = { ip: number; cardsState: AgentCardsState };

/**
 * Module-level cache — mirrors useProfile.ts's summaryCache pattern so
 * DrawPage and PackDetailPage (which key-remounts on every `:finish` change)
 * share one balance/catalog snapshot per signed-in user instead of each
 * independently refetching and flashing zero-state defaults on every
 * navigation within Draw. Unlike useProfile's fully-lazy cache, `load()`
 * below still silently re-fetches on every mount (stale-while-revalidate):
 * supply numbers here gate real purchases, so a merely-cached-forever value
 * could let a since-sold-out tier look purchasable again.
 */
let drawCache: { key: string; value: DrawSnapshot } | null = null;
let drawInflight: { key: string; promise: Promise<DrawSnapshot> } | null = null;
const drawListeners = new Set<() => void>();

function notifyDrawListeners() {
  for (const notify of drawListeners) notify();
}

function fetchDrawSnapshot(cacheKey: string): Promise<DrawSnapshot> {
  if (!drawInflight || drawInflight.key !== cacheKey) {
    const promise: Promise<DrawSnapshot> = Promise.all([
      api.getHomeSummary(),
      api.listAgentCards(),
    ])
      .then(([summary, cards]) => {
        // Same server response the profile dropdown would fetch — seed its
        // cache so the first dropdown open / TopBar isAdmin read is free.
        seedProfileSummary(cacheKey, { ip: summary.ip, rp: summary.rp, isAdmin: summary.isAdmin });
        return { ip: summary.ip, cardsState: cards };
      })
      .finally(() => {
        // Only clear if we're still the tracked inflight request — a newer
        // call for a different key may have already replaced us.
        if (drawInflight?.promise === promise) drawInflight = null;
      });
    drawInflight = { key: cacheKey, promise };
  }
  return drawInflight.promise;
}

/**
 * Shared Draw data source — IP balance + card catalog/supply state, cached
 * per signed-in user and patched in place from every PullGachaResult
 * (`result.state` already carries the post-pull supply/owned snapshot).
 *
 * A failed fetch (timeout, 5xx, dropped connection) never fabricates a zero
 * balance/collection as ground truth: prior cached state (if any) keeps
 * rendering, `loadError` flips true, and callers get `reload()` to retry —
 * see the "failed fetch silently renders as you own nothing" finding.
 */
export function useDrawData() {
  const { data: session, isPending: sessionPending } = authClient.useSession();
  const authenticated = resolveAuthenticated(session?.user);
  const cacheKey = session?.user?.id ?? "dev-bypass";

  const [, setVersion] = useState(0);
  const [dataLoading, setDataLoading] = useState(
    () => authenticated && drawCache?.key !== cacheKey,
  );
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    const notify = () => setVersion((v) => v + 1);
    drawListeners.add(notify);
    return () => {
      drawListeners.delete(notify);
    };
  }, []);

  const load = useCallback(
    (opts?: { silent?: boolean }) => {
      if (!authenticated) return;
      const silent = opts?.silent ?? false;
      // Silent (post-pull-failure) resyncs never touch loading/error UI — a
      // best-effort background reconcile shouldn't flicker the CTA row or
      // stack a second banner on top of whatever the overlay already shows.
      if (!silent) setDataLoading(drawCache?.key !== cacheKey);
      fetchDrawSnapshot(cacheKey)
        .then((value) => {
          drawCache = { key: cacheKey, value };
          setLoadError(false);
          notifyDrawListeners();
        })
        .catch(() => {
          if (!silent) setLoadError(true);
        })
        .finally(() => {
          if (!silent) setDataLoading(false);
        });
    },
    [authenticated, cacheKey],
  );

  useEffect(() => {
    if (sessionPending || !authenticated) {
      setDataLoading(false);
      return;
    }
    load();
  }, [authenticated, sessionPending, load]);

  const cached = authenticated && drawCache?.key === cacheKey ? drawCache.value : null;
  const ip = cached?.ip ?? 0;
  const cardsState = cached?.cardsState ?? EMPTY_CARDS_STATE;

  const applyPull = useCallback(
    (result: PullGachaResult) => {
      drawCache = { key: cacheKey, value: { ip: result.ipBalance, cardsState: result.state } };
      patchProfileSummary({ ip: result.ipBalance });
      // Post-pull snapshot is authoritative — push it into the shared
      // collection cache so Collection/Profile/Home render it instantly.
      seedAgentCardsCache(cacheKey, result.state);
      notifyDrawListeners();
    },
    [cacheKey],
  );

  return {
    authenticated,
    sessionPending,
    loading: sessionPending || dataLoading,
    loadError,
    ip,
    cardsState,
    applyPull,
    /** Manual retry (non-silent) for the inline "Couldn't load" banner. */
    reload: load,
  };
}

export type RevealState = {
  open: boolean;
  pending: boolean;
  cards: RevealCard[];
  error: string | null;
};

const REVEAL_CLOSED: RevealState = { open: false, pending: false, cards: [], error: null };

/**
 * NEW vs duplicate is decided against the *pre-pull* collection, walking the
 * pulled ids in order so the second copy of a brand-new card reads "×2".
 */
function buildRevealCards(cardIds: string[], preItems: AgentCardItem[]): RevealCard[] {
  const counts = new Map<string, number>();
  for (const item of preItems) counts.set(item.id, item.owned ? Math.max(item.count, 1) : 0);

  return cardIds.map((cardId, index) => {
    const prev = counts.get(cardId) ?? 0;
    counts.set(cardId, prev + 1);
    return { key: `${cardId}:${index}`, cardId, isNew: prev === 0, ownedCount: prev + 1 };
  });
}

/**
 * Known-safe, server-authored business messages (see pull_agent_card_gacha /
 * pull_daily_free_agent_card in agent_cards.rs) are shown verbatim; anything
 * else — a bare network TypeError, api.ts rpc()'s generic
 * "RPC x failed (status)" fallback, or a raw sqlx/driver string — gets a
 * reassuring generic message instead of leaking technical noise on the one
 * screen where real IP is spent.
 */
const SAFE_PULL_ERROR_PATTERNS = [
  /not enough ip/i,
  /requires an agent/i,
  /invalid agent/i,
  /invalid card draw tier/i,
  /already been claimed/i,
  /sold out/i,
];

export function friendlyPullError(err: unknown): string {
  const message = err instanceof Error ? err.message : "";
  if (message && SAFE_PULL_ERROR_PATTERNS.some((pattern) => pattern.test(message))) {
    return message;
  }
  return "Something went wrong opening this pack. If you were charged, your cards will appear in your Collection.";
}

/** Reveal-overlay driver — runPull opens the pending overlay, executes, applies the result. */
export function usePackReveal(
  preItems: AgentCardItem[],
  applyPull: (result: PullGachaResult) => void,
  /** Best-effort resync after a pull error — see useDrawData's `reload`. */
  reload?: (opts?: { silent?: boolean }) => void,
) {
  const [reveal, setReveal] = useState<RevealState>(REVEAL_CLOSED);
  // Generation counter — guards against a slow/hung pull's response landing
  // after a SECOND pull has already started on this page (reachable once the
  // 15s escape hatch lets a user cancel and immediately retry): only the
  // latest attempt's result may ever open/patch the overlay.
  const attemptIdRef = useRef(0);
  // Separate from the generation counter: set when the user dismisses a
  // still-pending attempt (Cancel) without starting a new one. The pull's
  // eventual data still lands (balance/cards must stay correct), it just
  // doesn't reopen the overlay the user already walked away from.
  const dismissedIdRef = useRef<number | null>(null);

  const runPull = useCallback(
    async (exec: () => Promise<PullGachaResult>) => {
      const id = ++attemptIdRef.current;
      setReveal({ open: true, pending: true, cards: [], error: null });
      try {
        const result = await exec();
        if (id !== attemptIdRef.current) return; // superseded by a newer pull
        applyPull(result);
        const n = result.cardIds.length;
        if (dismissedIdRef.current === id) {
          toast.success(`+${n} card${n === 1 ? "" : "s"} — check your Collection`);
          return;
        }
        setReveal({
          open: true,
          pending: false,
          cards: buildRevealCards(result.cardIds, preItems),
          error: null,
        });
        toast.success(`+${n} card${n === 1 ? "" : "s"}`);
      } catch (err) {
        // The RPC may have partially committed server-side even though the
        // client sees a failure (timeout, dropped connection) — resync
        // quietly so a stale balance/collection doesn't linger.
        reload?.({ silent: true });
        if (id !== attemptIdRef.current) return;
        const message = friendlyPullError(err);
        if (dismissedIdRef.current === id) {
          toast.error(message);
          return;
        }
        setReveal({ open: true, pending: false, cards: [], error: message });
      }
    },
    [preItems, applyPull, reload],
  );

  const closeReveal = useCallback(() => {
    setReveal((prev) => {
      if (prev.pending) dismissedIdRef.current = attemptIdRef.current;
      return REVEAL_CLOSED;
    });
  }, []);

  return { reveal, runPull, closeReveal };
}
